import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { isValid } from 'date-fns';
import { format } from 'date-fns';
import { NGXLogger } from 'ngx-logger';
import { chunk } from 'remeda';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, map, startWith, take, takeUntil, tap } from 'rxjs/operators';
import { ExportService } from '../../root/export.service';
import { NotificationsService } from '../../root/notifications.service';
import { RegionService } from '../../root/region.service';
import { SnackBarService } from '../../shared/snackbar.service';
import {
  Alert,
  BonusEvent,
  CalenderDayHitch,
  DriversHitchCalendarDay,
  DriversHitchStats,
  FracActivityReportPerRegion,
  FracActivityReportPerRegionWithQualifiedInfo,
  LastSettlementFees,
  NotificationType,
  Region,
  TierFeedsResponse,
  UserTierDetail,
  UserTierEvent,
} from '../../types';
import { OwnerDashboardService } from '../owner-dashboard.service';

@Component({
  selector: 'lohi-driver-information',
  templateUrl: './driver-information.component.html',
  styleUrls: ['./driver-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriverInformationComponent implements OnInit, OnDestroy {
  @ViewChild('crewModal') private crewsModalTemplate: TemplateRef<any>;
  private destroy$$ = new Subject();
  public fracsActivity$: Observable<FracActivityReportPerRegion[]>;
  public fracsActivityWithQualifiedInfo$: Observable<FracActivityReportPerRegionWithQualifiedInfo>;
  public selectedFracActivity$$ = new BehaviorSubject<FracActivityReportPerRegion>(null);
  public exporting$$ = new BehaviorSubject<boolean>(false);
  public exportFormGroup = new FormGroup({
    start: new FormControl(null, [Validators.required]),
    end: new FormControl(null, [Validators.required]),
  });
  public tierFeeds$: Observable<TierFeedsResponse>;
  public tierEvents$: Observable<UserTierEvent[]>;
  public tierDrivers$: Observable<UserTierDetail[]>;
  public filteredTierDrivers$$ = new ReplaySubject(1);
  public bonusEvents$: Observable<BonusEvent[]>;
  public finesAndFees$: Observable<LastSettlementFees>;
  public driversHitchStats$: Observable<DriversHitchStats>;
  public tierDropsEnabled$: Observable<NotificationType>;
  public tierDropControl = new FormControl();
  public alerts$: Observable<Alert[]>;
  public clearingAllAlerts$$ = new BehaviorSubject<boolean>(false);
  public tierDriverSearchText = new FormControl('');

  public today = new Date();
  public regionsFilterControl = new FormControl(-1);
  public regions$: Observable<Region[]>;
  public isDetailsViewOpen = false;
  public selectedDay: DriversHitchCalendarDay | null = null;
  public isShowArchivedCancelHitchAndShift$ = new BehaviorSubject<boolean>(false);
  public driversHitchCalendarWeeks$$ = new BehaviorSubject<DriversHitchCalendarDay[][]>([]);
  public driverHitchCalenderData: DriversHitchCalendarDay[];
  public isOpenEventToggle = false;
  public timeZoneAbbreviation: string;

  constructor(
    private dashboardService: OwnerDashboardService,
    private exportService: ExportService,
    private logger: NGXLogger,
    private snackBar: SnackBarService,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog,
    private notiService: NotificationsService,
    private router: Router,
    private regionService: RegionService,
  ) {}

  public ngOnInit(): void {
    this.bonusEvents$ = this.dashboardService.bonusEvents$;
    this.alerts$ = this.dashboardService.alerts$.pipe(map((alerts) => alerts?.filter((alert) => !alert.dismissed)));
    this.finesAndFees$ = this.dashboardService.lastFinesAndFeesThrottle$;
    this.fracsActivity$ = this.dashboardService.fracsActivity$.pipe(
      filter((value) => !!value),
      tap((value) => this.selectedFracActivity$$.next(value[0])),
    );
    this.fracsActivityWithQualifiedInfo$ = this.dashboardService.fracsActivityWithQualifiedInfo$.pipe(
      filter((value) => !!value),
    );
    this.tierFeeds$ = this.dashboardService.tierFeeds$;
    this.tierDrivers$ = this.dashboardService.tierDrivers$;
    combineLatest([this.tierDrivers$, this.tierDriverSearchText.valueChanges.pipe(startWith(''))])
      .pipe(
        map(([drivers, searchText]) => {
          if (!searchText) {
            return drivers;
          }
          return drivers.filter((driver) => {
            return (
              driver.userName.toLowerCase().includes(searchText.toLowerCase()) ||
              driver.userPhone.toLowerCase().includes(searchText.toLowerCase())
            );
          });
        }),
        takeUntil(this.destroy$$),
      )
      .subscribe((drivers) => {
        this.filteredTierDrivers$$.next(drivers);
      });
    this.driversHitchStats$ = this.dashboardService.driversHitchStats$;
    this.tierDropsEnabled$ = this.notiService.notificationTypes$.pipe(
      filter((value) => !!value),
      map((value) => value.filter((value1) => value1.name === 'driver_tier_drop')[0]),
    );
    this.setupRealControls();
    this.dashboardService.driversHitchCalendar$
      .pipe(
        takeUntil(this.destroy$$),
        filter((data) => !!data),
      )
      .subscribe((resp) => {
        this.driverHitchCalenderData = resp;
        this.driversHitchCalendarWeeks$$.next(chunk(resp, 7));
      });
    this.regions$ = this.regionService.regions$;
    this.setupFormControls();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    this.timeZoneAbbreviation = this.getTimeZoneAbbreviation(timezone);
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  public selectFracActivity(region: FracActivityReportPerRegion): FracActivityReportPerRegion {
    this.selectedFracActivity$$.next(region);
    return region;
  }

  public selectDriverTier(userID: string) {
    this.tierEvents$ = this.dashboardService.getDriverTierEvents$(userID);
  }

  public async exportData() {
    if (this.exporting$$.value) {
      return;
    }
    if (this.exportFormGroup.invalid) {
      this.exportFormGroup.markAllAsTouched();
      return;
    }
    const value: { start: Date; end: Date } = this.exportFormGroup.value;
    if (!isValid(value.start) || !isValid(value.end)) {
      return;
    }
    this.exporting$$.next(true);
    this.exportService.getBulkExportUrl$(value.start, value.end).subscribe(
      (urls) => {
        for (const url of urls) {
          window.open(url);
        }
        this.resetExportForm();
        this.exportFormGroup.markAsUntouched();
        this.exporting$$.next(false);
      },
      (error) => {
        this.logger.error(error);
        this.snackBar.showError('Failed to retrieve export');
        this.exporting$$.next(false);
      },
    );
  }

  public resetExportForm() {
    this.exportFormGroup = new FormGroup({
      start: new FormControl(null, [Validators.required]),
      end: new FormControl(null, [Validators.required]),
    });
    this.cd.markForCheck();
  }

  public getTierImage(tierId: number): string {
    switch (tierId) {
      case 4:
        return '/assets/images/tier-blue.svg';
      case 3:
        return '/assets/images/tier-silver.svg';
      case 2:
        return '/assets/images/tier-gold.svg';
      case 1:
        return '/assets/images/tier-black.svg';
      default:
        return '/assets/images/tier-blue.svg';
    }
  }

  public getTierColor(tierId: number): string {
    switch (tierId) {
      case 4:
        return '#448AFF';
      case 3:
        return '#6B767C';
      case 2:
        return '#AD8C3C';
      case 1:
        return '#2D2D2E';
      default:
        return '#448AFF';
    }
  }

  public openCrewsModal() {
    this.dialog.open(this.crewsModalTemplate, {
      autoFocus: false,
    });
  }

  private async setupRealControls() {
    const enabled = await this.tierDropsEnabled$.pipe(take(1)).toPromise();
    this.tierDropControl.setValue(enabled.enabled);

    this.tierDropControl.valueChanges.pipe(takeUntil(this.destroy$$)).subscribe((value) => {
      this.notiService.toggleNotification(enabled.id, value);
      this.snackBar.showMessage(`Driver Tier Notifications ${value ? 'Enabled' : 'Disabled'}`);
    });
  }

  public toggleTierNotification() {
    this.tierDropControl.setValue(!this.tierDropControl.value);
  }

  public dismissNotification(ids: number[]) {
    this.dashboardService.dismissAlerts$(ids).subscribe(
      (value) => {
        this.snackBar.showMessage('Alert Dismissed');
      },
      (error) => {
        this.snackBar.showError(error.error.messageToUser);
      },
    );
  }

  public dismissNotificationLocally(id) {
    this.dashboardService.dismissAlertLocally(id);
  }

  public clearAll() {
    this.clearingAllAlerts$$.next(true);
    this.dashboardService.clearAllAlerts$().subscribe(
      (value) => {
        this.clearingAllAlerts$$.next(false);
        this.snackBar.showMessage('All alerts cleared');
      },
      (error) => {
        this.clearingAllAlerts$$.next(false);
        this.snackBar.showError(error.error.messageToUser);
      },
    );
  }

  public redirectToDriverScorecard(id: string) {
    this.router.navigate(['drivers', id, 'scorecard']);
  }

  public getPenaltyAlertDescription(description: string): string {
    return description
      .split(' ')
      .splice(0, description.split(' ').length - 1)
      .join(' ');
  }

  public getPenaltyAlertDescriptionDriverNumber(description: string): string {
    return description.split(' ').splice(-1).join(' ');
  }

  public getPenaltyAlertTitleTrailerNumber(title: string): string {
    return title.split(' ').splice(0, 2).join(' ');
  }

  public getPenaltyAlertTitle(title: string): string {
    return title.split(' ').splice(2).join(' ');
  }

  public getActivityReportByRegionId(
    fracsPerRegion: FracActivityReportPerRegion[],
    regionId: number,
  ): FracActivityReportPerRegion {
    if (fracsPerRegion && fracsPerRegion.length > 0) {
      return fracsPerRegion.find((value) => value.regionId === regionId);
    }
    return null;
  }

  public toggleHitchDetails(day: DriversHitchCalendarDay): void {
    if (this.selectedDay === day) {
      this.isDetailsViewOpen = !this.isDetailsViewOpen;
    } else {
      this.selectedDay = day;
      this.isDetailsViewOpen = true;
    }
  }

  public onDateSelect(event: MatDatepickerInputEvent<Date>) {
    const formattedWithDateFns = format(event.value, 'yyyy-MM-dd\'T\'HH:mm:ssxxx');
    const filteredData = this.driverHitchCalenderData.filter((day) => day.date === formattedWithDateFns);
    this.toggleHitchDetails(filteredData[0]);
  }

  public get hitchesSelectedDay(): CalenderDayHitch[] {
    if (this.selectedDay) {
      const showArchived = this.isShowArchivedCancelHitchAndShift$.value;

      return [
        ...this.selectedDay.hitchesEndingToday.filter((hitch) => !hitch.archivedAt || showArchived),
        ...this.selectedDay.hitchesFullDay.filter((hitch) => !hitch.archivedAt || showArchived),
      ];
    }
    return [];
  }

  public toggleArchiveHitchShift(): void {
    const currentStatus = this.isShowArchivedCancelHitchAndShift$.value;
    this.isShowArchivedCancelHitchAndShift$.next(!currentStatus);
  }

  public getVisibleNoShowDrivers(): any[] {
    const showArchived = this.isShowArchivedCancelHitchAndShift$.value;

    if (!this.selectedDay.noShowDrivers) {
      return [];
    }

    return this.selectedDay.noShowDrivers.filter((driver) => !driver.archivedAt || showArchived);
  }

  public getVisibleExcusedDrivers(): any[] {
    const showArchived = this.isShowArchivedCancelHitchAndShift$.value;

    if (!this.selectedDay.excusedDrivers) {
      return [];
    }

    return this.selectedDay.excusedDrivers.filter((driver) => !driver.archivedAt || showArchived);
  }

  private setupFormControls() {
    this.regionsFilterControl.valueChanges.pipe(takeUntil(this.destroy$$)).subscribe((value) => {
      if (value === -1) {
        this.driversHitchCalendarWeeks$$.next(chunk(this.driverHitchCalenderData, 7));
      } else {
        this.driversHitchCalendarWeeks$$.next(
          chunk(
            this.driverHitchCalenderData.map((value1) => {
              return {
                ...value1,
                excusedDrivers: value1.excusedDrivers?.filter((driver) => driver.regionId === value),
                noShowDrivers: value1.noShowDrivers?.filter((driver) => driver.regionId === value),
                hitchesStartingToday: value1.hitchesStartingToday?.filter((hitch) => hitch.regionId === value),
                hitchesEndingToday: value1.hitchesEndingToday?.filter((hitch) => hitch.regionId === value),
                hitchesFullDay: value1.hitchesFullDay?.filter((hitch) => hitch.regionId === value),
                driverStatusCount: value1.driverStatusCount,
              };
            }),
            7,
          ),
        );
      }
    });
  }

  public toggleContentEvents(): void {
    this.isOpenEventToggle = !this.isOpenEventToggle;
  }

  public getTimeZoneAbbreviation(timezone): string {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      timeZoneName: 'short',
    };
    const timeZoneName = new Intl.DateTimeFormat('en-US', options).format(now);
    return timeZoneName.split(' ').pop() || '';
  }

  public hasNonArchivedHitch(): boolean {
    if (!this.selectedDay.noShowDrivers) {
      return false;
    }

    return this.selectedDay.noShowDrivers.some((driver) => !driver.archivedAt);
  }

  public trackByKeyHitch(index: number, hitch: any): number {
    return hitch.id;
  }

  public trackByKeyNoShowDriver(index: number, hitch: any): number {
    return hitch.id;
  }

  public trackByKeyExcusedDriver(index: number, hitch: any): number {
    return hitch.id;
  }

  public getContainerClass(hitchData: any): string {
    let classHitch = hitchData.isDayShift ? 'bg-yellow-300 text-black' : 'bg-black text-white';

    if (hitchData.archivedAt) {
      classHitch += ' opacity-25';
    }

    return classHitch;
  }

  public getMainIcon(hitchData: any): string {
    return hitchData.isDayShift ? 'light_mode' : 'dark_mode';
  }
}
